import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

export default function useDeepLink() {
  const router = useRouter();

  // Handle deep links for native OAuth callback
  // Supports both:
  // - wiswiz://aanmelden/callback?code=xxx (Microsoft, custom)
  // - com.googleusercontent.apps.{id}:/oauth2callback?code=xxx (Google iOS)
  // - com.wiswiz.app:/path?query (Google Android)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleDeepLink = async (event: URLOpenListenerEvent) => {
      try {
        // Close the in-app browser if it's open (important for OAuth flow)
        try {
          await Browser.close();
        } catch {
          // Browser might not be open, that's fine
        }

        // Check if it's a Google OAuth callback (iOS)
        if (event.url.startsWith("com.googleusercontent.apps.")) {
          // iOS Google format: com.googleusercontent.apps.{id}:/oauth2callback?code=xxx&state=xxx
          const queryStart = event.url.indexOf("?");
          if (queryStart !== -1) {
            router.replace(
              "/aanmelden/callback" + event.url.substring(queryStart)
            );
          }
          return;
        }

        // Check if it's an Android deep link (com.wiswiz.app:/ scheme)
        if (event.url.startsWith("com.wiswiz.app:/")) {
          // Android format: com.wiswiz.app:/path?query
          // Extract path and query after the scheme
          const withoutScheme = event.url.replace("com.wiswiz.app:/", "");
          const queryStart = withoutScheme.indexOf("?");
          if (queryStart !== -1) {
            const path = withoutScheme.substring(0, queryStart);
            const query = withoutScheme.substring(queryStart);
            router.replace(`/${path}${query}`);
          } else {
            router.replace(`/${withoutScheme}`);
          }
          return;
        }

        // Mollie payment return (Android)
        if (event.url.startsWith("wiswiz://payment/result")) {
          const url = new URL(event.url);
          const transactionId = url.searchParams.get("id");

          router.replace(
            transactionId
              ? `/payment/result?id=${transactionId}`
              : `/payment/result`
          );
          return;
        }

        // Parse standard URL: wiswiz://aanmelden/callback?code=xxx
        const url = new URL(event.url);
        router.replace(`/${url.host}${url.pathname}${url.search}`);
      } catch {
        router.replace("/aanmelden");
      }
    };

    // Listen for deep link events
    const listener = App.addListener("appUrlOpen", handleDeepLink);

    return () => {
      listener.then((l) => l.remove()).catch(() => {});
    };
  }, [router]);
}
